import { useLayoutEffect, useState } from 'react';
import { ProjectStatus } from '../../types';

export const KANBAN_ACTIVE_STATUSES: ProjectStatus[] = [
  'BRIEF_RECEIVED',
  'IN_PRODUCTION',
  'V1_SENT',
  'CHANGES_REQUESTED',
  'DELIVERED',
];

/** Min width used for full-board fit (status columns must all fit comfortably). */
export const KANBAN_COLUMN_MIN_WIDTH = 310;
export const KANBAN_COLUMN_GAP = 16;
/** Per-column floor in compact — higher = stay on fewer columns longer. */
export const KANBAN_COMPACT_COLUMN_MIN_WIDTH = 280;
export const KANBAN_COMPACT_COLUMN_GAP = 12;
/** Archive strip in full board (`w-20`). */
export const KANBAN_ARCHIVE_STRIP_WIDTH = 80;
/** Extra slack so full board doesn’t open then feel cramped. */
export const KANBAN_FULL_BOARD_SAFETY = 32;

export type KanbanLayout = {
  isFullBoard: boolean;
  /** Columns shown in compact window (1–4). Ignored when isFullBoard. */
  visibleColumnCount: number;
};

/** Width needed for full board: 5 status columns + archive strip + gaps. */
export function fullBoardWidthNeeded(hasArchiveStrip: boolean): number {
  const n = KANBAN_ACTIVE_STATUSES.length;
  const columns =
    n * KANBAN_COLUMN_MIN_WIDTH + (n - 1) * KANBAN_COLUMN_GAP;
  const archive = hasArchiveStrip
    ? KANBAN_ARCHIVE_STRIP_WIDTH + KANBAN_COLUMN_GAP
    : 0;
  return columns + archive + KANBAN_FULL_BOARD_SAFETY;
}

/**
 * Full board only when every status column AND the archive strip fit.
 * Otherwise compact with as many columns as width allows (capped at 4).
 */
export function computeKanbanLayout(
  width: number,
  options: { hasArchiveStrip: boolean }
): KanbanLayout {
  if (width <= 0) {
    return { isFullBoard: false, visibleColumnCount: 1 };
  }

  const n = KANBAN_ACTIVE_STATUSES.length;
  // Full board only when status columns + archive strip (when present) all fit.
  const fullNeeded = fullBoardWidthNeeded(options.hasArchiveStrip);

  if (width >= fullNeeded) {
    return { isFullBoard: true, visibleColumnCount: n };
  }

  const count = Math.max(
    1,
    Math.min(
      n - 1,
      Math.floor(
        (width + KANBAN_COMPACT_COLUMN_GAP) /
          (KANBAN_COMPACT_COLUMN_MIN_WIDTH + KANBAN_COMPACT_COLUMN_GAP)
      )
    )
  );

  return { isFullBoard: false, visibleColumnCount: count };
}

/** Sliding window of `count` statuses, anchored on focus (slides back near the end). */
export function getKanbanVisibleStatuses(
  focus: ProjectStatus,
  count: number
): ProjectStatus[] {
  const safeCount = Math.max(1, Math.min(count, KANBAN_ACTIVE_STATUSES.length));
  const focusIndex = Math.max(0, KANBAN_ACTIVE_STATUSES.indexOf(focus));
  const maxStart = KANBAN_ACTIVE_STATUSES.length - safeCount;
  const start = Math.min(focusIndex, Math.max(0, maxStart));
  return KANBAN_ACTIVE_STATUSES.slice(start, start + safeCount);
}

/** Observe element content width (ResizeObserver). Use the returned callback as `ref`. */
export function useMeasuredWidth(): {
  ref: (node: HTMLElement | null) => void;
  width: number;
} {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);

  // Measure before paint to avoid compact-layout flash (e.g. archived → kanban remount).
  useLayoutEffect(() => {
    if (!node) {
      setWidth(0);
      return;
    }

    const update = () => setWidth(node.clientWidth);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setWidth(w);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, width };
}
